import dayjs from 'dayjs';
import type { BuybackReceipt } from '../types/buyback';

const esc = (s: string | null | undefined): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));

const won = (n: number | null | undefined): string =>
  n == null ? '-' : `${n.toLocaleString('ko-KR')}원`;

const dt = (s: string | null | undefined): string =>
  s ? dayjs(s).format('YYYY-MM-DD HH:mm') : '-';

/**
 * 매입 영수증을 새 창에 인쇄용 HTML 로 그려 바로 인쇄 대화상자를 띄운다. 앱 레이아웃/테마와
 * 분리된 독립 문서라 인쇄 스타일이 섞이지 않는다. 개인정보(고객명·전화)는 담지 않으며,
 * 거래는 관리번호로 특정한다.
 */
export function printBuybackReceipt(r: BuybackReceipt): boolean {
  const rows = r.items.map((it) => `
    <tr>
      <td>${esc(it.productName)}</td>
      <td class="num">${it.quantity}</td>
      <td class="num">${won(it.unitPrice)}</td>
      <td class="num">${won(it.amount)}</td>
    </tr>`).join('');

  const html = `<!doctype html>
<html lang="ko"><head><meta charset="utf-8" />
<title>매입 영수증 ${esc(r.managementNumber ?? '')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Pretendard', -apple-system, 'Malgun Gothic', sans-serif; color: #1a1a1a;
         margin: 0; padding: 24px; }
  .sheet { max-width: 420px; margin: 0 auto; }
  h1 { font-size: 18px; text-align: center; margin: 0 0 4px; letter-spacing: .04em; }
  .shop { text-align: center; font-size: 12px; color: #444; line-height: 1.5; margin-bottom: 14px; }
  .meta { font-size: 12px; color: #333; border-top: 1px solid #000; border-bottom: 1px solid #ccc;
          padding: 8px 0; margin-bottom: 8px; }
  .meta div { display: flex; justify-content: space-between; margin: 2px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { padding: 6px 4px; border-bottom: 1px solid #eee; text-align: left; }
  th { border-bottom: 1px solid #000; font-weight: 600; }
  .num { text-align: right; white-space: nowrap; }
  .total { display: flex; justify-content: space-between; align-items: baseline;
           border-top: 2px solid #000; margin-top: 6px; padding-top: 8px; font-weight: 700; }
  .total .amt { font-size: 18px; }
  .note { margin-top: 16px; font-size: 11px; color: #777; text-align: center; line-height: 1.6; }
  @media print { body { padding: 0; } @page { margin: 12mm; } }
</style></head>
<body><div class="sheet">
  <h1>매입 영수증</h1>
  <div class="shop">
    <strong>${esc(r.shop.name)}</strong><br/>
    사업자번호 ${esc(r.shop.businessNumber)} · 대표 ${esc(r.shop.owner)}<br/>
    ${esc(r.shop.address)}<br/>
    Tel. ${esc(r.shop.phone)}
  </div>
  <div class="meta">
    <div><span>관리번호</span><span>${esc(r.managementNumber ?? '-')}</span></div>
    <div><span>매입일시</span><span>${dt(r.completedAt)}</span></div>
    <div><span>발행일시</span><span>${dt(r.issuedAt)}</span></div>
  </div>
  <table>
    <thead><tr><th>상품명</th><th class="num">수량</th><th class="num">단가</th><th class="num">금액</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="total"><span>매입 총액</span><span class="amt">${won(r.totalAmount)}</span></div>
  <div class="note">본 영수증은 매입 거래 내역 확인용입니다.<br/>문의: 관리번호와 함께 매장으로 연락 주세요.</div>
</div>
<script>window.onload = function () { window.print(); };</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=480,height=720');
  if (!w) return false; // 팝업 차단 — 호출부에서 안내
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
